import type { ChangeEvent } from 'react'
import { Box } from '@mui/material'
import { CheckboxInput, Text, TextInput } from 'ui-kit'
import { PhoneField } from '../Forms/PhoneField'
import { SelectInput } from '../Forms/SelectInput'
import type {
  LeadFormField as LeadFormFieldConfig,
  LeadFormSurface,
} from './types'

type LeadFormFieldProps = {
  field: LeadFormFieldConfig
  id: string
  value: string | boolean
  error?: string
  surface: LeadFormSurface
  onChange: (value: string | boolean) => void
}

const fieldBox = {
  light: { mb: 2 },
  dark: { mb: 2, width: { md: '60%' }, mx: 'auto' },
}

export function LeadFormField({
  field,
  id,
  value,
  error,
  surface,
  onChange,
}: LeadFormFieldProps) {
  const { salesforceName: name, label, required, helperText } = field
  const isDark = surface === 'dark'
  const textValue = typeof value === 'string' ? value : ''
  const textInputProps = {
    id,
    label,
    required,
    value: textValue,
    error,
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      onChange(event.target.value),
    ...(isDark
      ? { variant: 'filled' as const, surface: 'inverse' as const }
      : {}),
  }

  const helper = helperText ? (
    <Text
      variant="p3"
      component="p"
      color={isDark ? 'white' : 'darkGray'}
      sx={{ mt: -1, mb: 2, textAlign: 'left' }}
    >
      {helperText}
    </Text>
  ) : null

  switch (field.type) {
    case 'hidden':
      return null
    case 'select':
      return (
        <Box sx={fieldBox[surface]}>
          <SelectInput
            id={id}
            name={name}
            label={label}
            required={required}
            value={textValue}
            onChange={event => onChange(event.target.value)}
            options={field.options.map(option => ({
              value: option.value,
              title: option.label,
            }))}
            error={error}
            sx={{ mb: helper ? 1 : 2 }}
          />
          {helper}
        </Box>
      )
    case 'checkbox':
      return (
        <Box sx={{ ...fieldBox[surface], textAlign: 'left' }}>
          <CheckboxInput
            id={id}
            name={name}
            label={required ? `${label}*` : label}
            value={value === true}
            checked={value === true}
            onChange={
              ((event: ChangeEvent<HTMLInputElement>) =>
                onChange(event.target.checked)) as () => void
            }
            surface={isDark ? 'inverse' : 'default'}
          />
          {error ? (
            <Text variant="p3" component="p" color="red">
              {error}
            </Text>
          ) : null}
          {helper}
        </Box>
      )
    case 'phone':
      if (!isDark) {
        return (
          <>
            <PhoneField
              id={id}
              name={name}
              label={label}
              required={required}
              value={textValue}
              onChange={onChange}
              error={error}
            />
            {helper}
          </>
        )
      }
      return (
        <Box sx={fieldBox[surface]}>
          <TextInput
            {...textInputProps}
            inputProps={{ name, inputMode: 'tel', autoComplete: 'tel' }}
          />
          {helper}
        </Box>
      )
    default:
      return (
        <Box sx={fieldBox[surface]}>
          <TextInput
            {...textInputProps}
            type={field.type === 'email' ? 'email' : 'text'}
            multiline={field.type === 'textarea'}
            rows={field.type === 'textarea' ? 7 : undefined}
            inputProps={{ name }}
          />
          {helper}
        </Box>
      )
  }
}
